'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Image from '@components/common/ImageFallback';
import { getLang, useTranslation } from '@root/src/i18n/client';
import { getAllCategory } from '@root/src/services/client/category';
import {
  SharedSectionBackground,
  sharedSectionRootStyle,
} from '../sharedSectionBackground';

interface ApiCategoryItem {
  id?: string | number;
  slug?: string;
  parentId?: string | number | null;
  name?: string;
  nameEn?: string;
  nameZh?: string;
  description?: string;
  descriptionEn?: string;
  descriptionZh?: string;
  image?: string | string[];
}

interface TeaCategoryItem {
  id: string;
  slug: string;
  name: string;
  description: string;
  image: string;
  isPlaceholder?: boolean;
}

const APPEAR_URL = process.env.NEXT_PUBLIC_APPEAR || 'https://tramhuongmocphucduyen.com';

function pickLocalizedValue(
  item: ApiCategoryItem,
  lang: string,
  field: 'name' | 'description'
) {
  if (lang === 'en') {
    return item[`${field}En` as 'nameEn' | 'descriptionEn'] || item[field] || '';
  }
  if (lang === 'zh') {
    return item[`${field}Zh` as 'nameZh' | 'descriptionZh'] || item[field] || '';
  }
  return item[field] || '';
}

function extractImage(image?: string | string[]) {
  if (Array.isArray(image)) {
    return image[0] || '';
  }
  return image || '';
}

function limitText(value: string, maxLength: number) {
  if (!value) return '';
  const normalized = value.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength).trim()}...`;
}

function mapCategories(items: ApiCategoryItem[], lang: string) {
  return items
    .filter(item => item?.slug)
    .map(item => ({
      id: String(item.id || item.slug),
      slug: String(item.slug),
      name: limitText(pickLocalizedValue(item, lang, 'name') || String(item.slug), 36),
      description: limitText(pickLocalizedValue(item, lang, 'description'), 64),
      image: extractImage(item.image),
    }));
}

export default function TeaCategory() {
  const lang = getLang();
  const { t } = useTranslation('common');
  const [categories, setCategories] = useState<TeaCategoryItem[]>([]);

  useEffect(() => {
    let mounted = true;

    const loadCategories = async () => {
      const res = await getAllCategory(undefined, APPEAR_URL);
      const items = Array.isArray(res?.data) ? mapCategories(res.data, lang) : [];

      if (mounted) {
        setCategories(items.slice(0, 6));
      }
    };

    loadCategories();

    return () => {
      mounted = false;
    };
  }, [lang]);

  const subtitle = t('lytra.homeCategoryBadge');
  const mainTitle = t('lytra.homeCategoryTitle');
  const ctaText = t('lytra.homeCategoryCta');
  const categoryItemsToRender: TeaCategoryItem[] = categories.length
    ? categories
    : Array.from({ length: 6 }, (_, index) => ({
      id: `placeholder-${index + 1}`,
      slug: '',
      name: '',
      description: '',
      image: '',
      isPlaceholder: true,
    }));

  return (
    <section
      className="relative overflow-hidden px-4 py-10 sm:px-6 sm:py-12 lg:px-8 lg:py-14"
      style={sharedSectionRootStyle}
    >
      <SharedSectionBackground
        priority
        quality={60}
        sizes="(max-width: 768px) 440px, 690px"
      />

      <div className="absolute left-0 right-0 top-0 flex justify-center">
        <div
          className="h-px w-24 sm:w-32"
          style={{
            background:
              'linear-gradient(to right, transparent, #c6a869, transparent)',
          }}
        />
      </div>

      <div
        className="pointer-events-none absolute left-4 top-6 opacity-20"
        style={{ fontSize: '2.5rem', color: '#7a9e6e' }}
      >
        🌿
      </div>
      <div
        className="pointer-events-none absolute right-4 top-6 opacity-20"
        style={{ fontSize: '2.5rem', color: '#7a9e6e', transform: 'scaleX(-1)' }}
      >
        🌿
      </div>

      <div className="relative z-10 mx-auto mb-8 max-w-2xl text-center sm:mb-10 lg:mb-8">
        <div
          className="mb-3 inline-block rounded-full px-4 py-1.5"
          style={{ background: 'rgba(198,168,105,0.10)' }}
        >
          <span
            className="text-[11px] font-semibold uppercase sm:text-xs"
            style={{
              letterSpacing: '0.2em',
              color: '#c6a869',
            }}
          >
            {subtitle}
          </span>
        </div>

        <h2
          className="mt-4 text-2xl leading-tight sm:text-4xl lg:text-5xl"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            fontWeight: 400,
            color: '#2f3c30',
          }}
        >
          {mainTitle}
        </h2>

        <div className="mt-4 flex items-center justify-center gap-3">
          <span
            className="h-px w-10 sm:w-12"
            style={{
              background: 'linear-gradient(to right, transparent, #d8c6a1)',
            }}
          />
          <span style={{ fontSize: '1.125rem', color: '#d2b57a' }}>✦</span>
          <span
            className="h-px w-10 sm:w-12"
            style={{
              background: 'linear-gradient(to left, transparent, #d8c6a1)',
            }}
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto w-full max-w-[1320px]">
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 xl:gap-8">
          {categoryItemsToRender.map(item => (
            <CategoryCardItem key={item.id} item={item} />
          ))}
        </div>
      </div>

      <div className="relative z-10 mt-8 flex justify-center sm:mt-10 lg:mt-8">
        <Link
          href="/all-category"
          className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-xs font-semibold uppercase tracking-[0.15em] transition-all duration-300 sm:px-8 sm:text-sm"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            background: 'transparent',
            color: 'rgb(102, 115, 72)',
            border: '2px solid rgb(102, 115, 72)',
            textDecoration: 'none',
          }}
        >
          {ctaText}
          <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
            →
          </span>
        </Link>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 sm:bottom-8">
        <div className="flex gap-2">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="h-1 w-1 rounded-full"
              style={{ background: 'rgba(198,168,105,0.4)' }}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function CategoryCardItem({ item }: { item: TeaCategoryItem }) {
  if (item.isPlaceholder) {
    return (
      <div className="flex w-full flex-col items-center gap-3 text-center" aria-hidden="true">
        <div className="relative aspect-square w-full max-w-[138px] animate-pulse overflow-hidden rounded-[26px] border-[3px] border-[#d8c6a1] bg-[#efe4cf] outline outline-2 outline-offset-4 outline-[#c6a869]/20 sm:h-[158px] sm:w-[158px] sm:max-w-[158px] sm:rounded-full" />
        <div className="space-y-2">
          <div className="mx-auto h-5 w-20 animate-pulse rounded-full bg-[#dfd0b1] sm:h-6 sm:w-24" />
          <div className="mx-auto h-4 w-24 animate-pulse rounded-full bg-[#ece1cc] sm:w-28" />
        </div>
      </div>
    );
  }

  return (
    <Link
      href={`/${item.slug}`}
      className="group flex w-full flex-col items-center gap-3 text-center"
      style={{ textDecoration: 'none' }}
    >
      <div className="relative w-full max-w-[138px] sm:h-[158px] sm:w-[158px] sm:max-w-[158px]">
        <div
          className="absolute inset-0 rounded-[28px] transition-transform duration-500 group-hover:scale-105 sm:rounded-full"
          style={{
            background:
              'linear-gradient(135deg, #e8d5a3 0%, #c6a869 50%, #a8855a 100%)',
            padding: '2px',
          }}
        >
          <div
            className="h-full w-full rounded-[26px] sm:rounded-full"
            style={{ background: '#f7f0e3' }}
          />
        </div>

        <div
          className="relative aspect-square w-full overflow-hidden rounded-[26px] transition-all duration-500 group-hover:shadow-lg sm:h-[158px] sm:w-[158px] sm:rounded-full"
          style={{
            border: '3px solid #d8c6a1',
            outline: '2px solid rgba(198,168,105,0.25)',
            outlineOffset: '4px',
          }}
        >
          {item.image ? (
            <Image
              src={item.image}
              alt={item.name}
              fill
              className="object-cover transition-transform duration-500 group-hover:scale-110"
              sizes="(max-width: 640px) 138px, 158px"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center"
              style={{
                background:
                  'linear-gradient(135deg, #e8d5a3 0%, #c9a96e 100%)',
              }}
            >
              <span style={{ fontSize: '2rem' }}>🍃</span>
            </div>
          )}

          <div
            className="absolute inset-0 rounded-[26px] opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:rounded-full"
            style={{
              background:
                'linear-gradient(135deg, rgba(198,168,105,0.20) 0%, rgba(47,60,48,0.15) 100%)',
            }}
          />
        </div>
      </div>

      <div className="space-y-1">
        <p
          className="mx-auto line-clamp-2 max-w-[132px] text-lg font-semibold leading-snug transition-colors duration-300 group-hover:text-[#c6a869] sm:max-w-[158px] sm:text-[24px]"
          style={{
            fontFamily: 'Georgia, "Times New Roman", serif',
            color: '#2f3c30',
          }}
        >
          {item.name}
        </p>
        {item.description && (
          <p
            className="mx-auto line-clamp-2 max-w-[132px] text-[12px] leading-snug sm:max-w-[132px] sm:text-[13px]"
            style={{ color: '#8a7d6b' }}
          >
            {item.description}
          </p>
        )}
      </div>
    </Link>
  );
}
